import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { pushError } from 'src/utilities/notifications';
import { MediaTypes, saveFile } from 'src/utilities/saveFile';
import { AsyncTaskStatus } from 'src/utilities/useAsyncTask';
import { useAsyncDownloadTask } from 'src/utilities/useAsyncDownloadTask/useAsyncDownloadTask';
import {
  getEmployeesCsvFromS3,
  requestEmployeesCsvDownload,
} from 'src/components/enterprise/features/Employee/EmployeeListPage/api/downloadEmployeesCsvApi';

export const MAX_DOWNLOAD_ATTEMPTS = 4;
export const FILENAME_DATE_FORMAT = 'MMddyyyy';
export const DOWNLOAD_ERROR_MESSAGE =
  "We're having trouble downloading your CSV. Please try again later or contact your account manager.";

export interface UseDownloadEmployeesCsvReturn {
  downloadEmployeesCsv: () => Promise<void>;
  isDownloading: boolean;
}

export function useDownloadEmployeesCsv(): UseDownloadEmployeesCsvReturn {
  const { corpId, corpData } = useAuth();
  const { status, downloadUrl, startAsyncTask } = useAsyncDownloadTask();
  const [isDownloading, setIsDownloading] = useState(false);
  const failedAttempts = useRef(0);

  const corpName = corpData?.name;

  const requestDownload = useCallback(async (): Promise<void> => {
    // A failed request is also reported through the ERROR task status, which is
    // the single signal the retry below reacts to.
    await startAsyncTask(() => requestEmployeesCsvDownload(corpId)).catch(
      () => undefined
    );
  }, [corpId, startAsyncTask]);

  const retryDownload = useCallback((): void => {
    failedAttempts.current += 1;

    if (failedAttempts.current < MAX_DOWNLOAD_ATTEMPTS) {
      requestDownload();
      return;
    }

    failedAttempts.current = 0;
    setIsDownloading(false);
    pushError(DOWNLOAD_ERROR_MESSAGE);
  }, [requestDownload]);

  const saveEmployeesCsv = useCallback(
    async (url: string): Promise<void> => {
      try {
        const csv = await getEmployeesCsvFromS3(url);

        if (!csv) {
          retryDownload();
          return;
        }

        const today = format(new Date(), FILENAME_DATE_FORMAT);

        saveFile(
          csv,
          `${corpName}_Employee_List_${today}.csv`,
          MediaTypes.TEXT_CSV
        );
        failedAttempts.current = 0;
        setIsDownloading(false);
      } catch {
        retryDownload();
      }
    },
    [corpName, retryDownload]
  );

  useEffect(() => {
    if (!isDownloading) {
      return;
    }

    if (status === AsyncTaskStatus.ERROR) {
      retryDownload();
      return;
    }

    if (status === AsyncTaskStatus.COMPLETE) {
      if (downloadUrl) {
        saveEmployeesCsv(downloadUrl);
      } else {
        retryDownload();
      }
    }
  }, [status, downloadUrl, isDownloading, retryDownload, saveEmployeesCsv]);

  const downloadEmployeesCsv = useCallback(async (): Promise<void> => {
    failedAttempts.current = 0;
    setIsDownloading(true);

    await requestDownload();
  }, [requestDownload]);

  return {
    downloadEmployeesCsv,
    isDownloading,
  };
}
